import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { postRequestAuthenticated } from "../../../services/rest";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { FiCheck, FiCopy, FiGlobe, FiAlertCircle } from "react-icons/fi";
import DNSTable from "../../Integration/widget";

const hostFromUrl = (u = "") => {
  try {
    return u
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0];
  } catch {
    return u;
  }
};

const METHODS = [
  { key: "dns", label: "DNS TXT record", hint: "Recommended · permanent" },
  { key: "file", label: "File upload", hint: "Place a file on your server" },
  { key: "meta", label: "Meta tag", hint: "Add a tag to your homepage" },
];

const DomainStep = ({ onVerified }) => {
  const business = useSelector((s) => s.business.business);
  const [domain, setDomain] = useState("");
  const [method, setMethod] = useState("dns");
  const [token, setToken] = useState(null);
  const [provider, setProvider] = useState(null);
  const [verified, setVerified] = useState(false);
  const [stage, setStage] = useState("init"); // init | entry | methods
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await postRequestAuthenticated("/check/initial/api/key");
        if (res.success) {
          if (res.apiKey || res.page >= 2) {
            setVerified(true);
            setDomain(res.domain || "");
            setStage("methods");
            return;
          }
          if (res.page === 1 && res.domain) {
            setDomain(res.domain);
            setToken(res.verificationToken);
            setProvider(res.provider);
            setMethod(res.verificationMethod || "dns");
            setStage("methods");
            return;
          }
        }
      } catch (e) {
        /* fall through to entry */
      }
      setDomain(hostFromUrl(business?.website || ""));
      setStage("entry");
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRecord = async () => {
    setError("");
    if (!domain || domain.length < 4) {
      setError("Enter a valid domain.");
      return;
    }
    setLoading(true);
    try {
      const res = await postRequestAuthenticated("/check/domain/verification", {
        domain,
      });
      if (res.success) {
        setToken(res.token);
        setProvider(res.provider);
        setStage("methods");
      } else {
        setError(res.message || "Could not start verification.");
      }
    } catch (e) {
      setError("Could not start verification.");
    } finally {
      setLoading(false);
    }
  };

  const checkNow = async () => {
    setError("");
    setChecking(true);
    try {
      const res = await postRequestAuthenticated("/domain/verification/method", {
        verificationMethod: method,
      });
      if (res.success) {
        // show an auto-closing success dialog, then advance
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setVerified(true);
          if (onVerified) onVerified();
        }, 2200);
      } else {
        setError(res.message || "Not verified yet — give DNS a few minutes.");
      }
    } catch (e) {
      setError("Verification failed. Try again in a few minutes.");
    } finally {
      setChecking(false);
    }
  };

  const metaTag = `<meta name="channels-verification" content="${token}"/>`;
  const fileUrl = `https://api.channels.social/api/download/verification-file?token=${encodeURIComponent(
    token || ""
  )}`;

  if (stage === "init") {
    return (
      <div className="mx-auto max-w-2xl py-10 text-sm text-theme-placeholder">
        Loading…
      </div>
    );
  }

  if (verified) {
    return (
      <div className="mx-auto max-w-2xl py-10 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-theme-success text-white">
          <FiCheck size={22} />
        </div>
        <h2 className="text-xl font-semibold text-theme-secondaryText">
          Domain verified
        </h2>
        <p className="mt-1 text-sm text-theme-placeholder">
          {domain} is verified — your embed is unlocked.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      {/* Auto-closing success dialog shown right after a domain verifies */}
      {showSuccess && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="mx-4 flex w-full max-w-sm flex-col items-center rounded-2xl border border-theme-chatDivider bg-theme-tertiaryBackground px-6 py-8 text-center shadow-2xl">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-theme-success/15">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-theme-success text-white">
                <FiCheck size={28} strokeWidth={3} />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-theme-secondaryText">
              Domain verified
            </h2>
            <p className="mt-1 text-sm text-theme-placeholder">
              {domain} is verified — your embed is unlocked.
            </p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-theme-secondaryText">
          Verify your domain
        </h2>
        <p className="mt-1 text-sm text-theme-placeholder">
          Confirm ownership of your domain to unlock the embed.
        </p>
      </div>

      {/* Domain entry */}
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
        Your domain
      </label>
      <div className="flex items-stretch gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3">
          <FiGlobe className="text-theme-placeholder" size={16} />
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={stage === "methods"}
            placeholder="acmecoffee.com"
            className="w-full bg-transparent py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder disabled:opacity-70"
          />
        </div>
        {stage === "entry" && (
          <button
            onClick={getRecord}
            disabled={loading}
            className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover disabled:opacity-50"
          >
            {loading ? "…" : "Continue"}
          </button>
        )}
      </div>

      {stage === "methods" && (
        <div className="mt-6">
          {/* method cards */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {METHODS.map((m) => {
              const on = method === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setMethod(m.key)}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    on
                      ? "border-theme-accent bg-theme-accentSoft"
                      : "border-theme-chatDivider bg-theme-secondaryBackground hover:border-theme-placeholder"
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${
                      on ? "text-theme-accent" : "text-theme-secondaryText"
                    }`}
                  >
                    {m.label}
                  </p>
                  <p className="mt-0.5 text-xs text-theme-placeholder">
                    {m.hint}
                  </p>
                </button>
              );
            })}
          </div>

          {/* method content */}
          <div className="mt-5 rounded-xl border border-theme-chatDivider bg-theme-secondaryBackground p-4">
            {method === "dns" && (
              <div className="space-y-3 text-sm text-theme-primaryText">
                <p className="text-theme-secondaryText">
                  Add this TXT record in your DNS{" "}
                  {provider ? `(${provider})` : ""}:
                </p>
                <DNSTable token={token} />
              </div>
            )}
            {method === "file" && (
              <div className="space-y-2 text-sm text-theme-primaryText">
                <p className="text-theme-secondaryText">
                  Download and upload this file to your site root:
                </p>
                <a
                  href={fileUrl}
                  className="inline-block text-theme-accent underline"
                >
                  channels-verification.txt
                </a>
                <p className="text-xs text-theme-placeholder">
                  It should be reachable at https://{domain}
                  /channels-verification.txt
                </p>
              </div>
            )}
            {method === "meta" && (
              <div className="space-y-2 text-sm text-theme-primaryText">
                <p className="text-theme-secondaryText">
                  Add this tag inside the &lt;head&gt; of {domain}:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 overflow-x-auto rounded-lg bg-theme-tertiaryBackground px-3 py-2 font-mono text-xs text-theme-secondaryText">
                    {metaTag}
                  </code>
                  <CopyToClipboard
                    text={metaTag}
                    onCopy={() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1200);
                    }}
                  >
                    <button className="flex items-center gap-1.5 rounded-lg border border-theme-chatDivider px-3 py-2 text-xs font-semibold text-theme-primaryText hover:bg-theme-tertiaryBackground">
                      {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </CopyToClipboard>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border-l-2 border-theme-accent bg-theme-accentSoft px-3 py-2.5 text-xs text-theme-secondaryText">
            DNS changes can take 5–30 minutes to propagate. Add the record
            above, then click “Check now”. You can safely close this and come
            back — we'll pick up right here once your domain verifies.
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={checkNow}
              disabled={checking}
              className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover disabled:opacity-50"
            >
              {checking ? "Checking…" : "Check now"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-theme-warning">
          <FiAlertCircle size={15} /> {error}
        </p>
      )}
    </div>
  );
};

export default DomainStep;
